import type { ComponentType } from "react";
import { useState } from "react";

import { signalTypeMap } from "app/pages/sales-intelligence/domain/signals/types/signal";
import type { SignalSource, SignalType, SignalFilters } from "app/pages/sales-intelligence/domain/signals/types/signal";
import SignalValidationBanner from "app/pages/sales-intelligence/sub-modules/signals/components/shared/SignalValidationBanner/SignalValidationBanner";
import useSignalValidation from "app/pages/sales-intelligence/sub-modules/signals/hooks/useSignalValidation";
import type {
    SignalFiltersViewBaseProps,
    SubmitRendererProps,
} from "app/pages/sales-intelligence/sub-modules/signals/models/view/SignalFiltersViewProps";

const withSignalValidation = <P extends SignalFiltersViewBaseProps<SignalFilters>>(
    ConsumerComponent: ComponentType<P>,
    signalType: SignalType,
    signalSource: SignalSource,
): ComponentType<P> => {
    return function WrapperWithSignalValidation(props: P) {
        const [isSignalTouched, setIsSignalTouched] = useState(false);
        const [isSignalValid, onSignalValidation] = useSignalValidation({
            signalType: signalType,
            signalSubType: props.signalSubType,
            filters: props.filters,
            signalSource,
        });

        const renderSubmit = (submitProps: SubmitRendererProps) => {
            return props.renderSubmit({
                ...submitProps,
                isSignalValid,
            });
        };

        const handleFiltersChange = (filters: Readonly<SignalFilters>) => {
            if (!isSignalTouched) {
                setIsSignalTouched(true);
            }

            onSignalValidation(filters);
            props.onFiltersChange?.(filters);
        };

        const isVisible: boolean = (isSignalTouched || signalType === signalTypeMap.news) && !isSignalValid;

        return (
            <ConsumerComponent
                {...props}
                notificationView={<SignalValidationBanner isVisible={isVisible} />}
                renderSubmit={renderSubmit}
                onFiltersChange={handleFiltersChange}
            />
        );
    };
};

export default withSignalValidation;
